import { useDashboard } from 'api/company/useDashboard'
import { useFocusCustomers } from 'api/company/useFocusCustomers'
import { MainPage } from 'components/templates/MainPage'

export const DashboardPage = (): JSX.Element => {
  const { data } = useDashboard()
  const { data: focusCustomers } = useFocusCustomers()

  return (
    <MainPage>
      <div className={'space-y-5'}>
        {/* KPIカード（今月売上、今年の売上、商談数、成約率） */}
        <div className={'grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4'}>
          {data?.kpis.map((kpi) => {
            return (
              <div
                key={kpi.label}
                className={'rounded-md border bg-white p-4 shadow-sm'}
              >
                <div className={'text-sm text-gray-500'}>{kpi.label}</div>
                <div className={'mt-2 text-3xl font-bold text-gray-800'}>
                  {kpi.value}
                </div>
                <div className={'mt-1 text-xs text-gray-500'}>
                  {kpi.target} ({kpi.achievementRate}%)
                </div>
                <div className={'mt-3 h-2 overflow-hidden rounded bg-gray-200'}>
                  <div
                    className={'h-full bg-green'}
                    style={{ width: `${kpi.achievementRate}%` }}
                  />
                </div>
                <div
                  className={`mt-2 text-xs font-semibold ${
                    kpi.trendType === 'up' ? 'text-emerald-600' : 'text-red-500'
                  }`}
                >
                  {kpi.trendText}
                </div>
              </div>
            )
          })}
        </div>

        {/* 要員稼働率・平均単価・待機要員・新規顧客獲得 */}
        <div className={'grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4'}>
          <div className={'rounded-md border bg-white p-4 shadow-sm'}>
            <div className={'text-sm text-gray-500'}>要員稼働率</div>
            <div className={'mt-2 text-3xl font-bold text-gray-800'}>
              {data?.staffUtilization.rate}%
            </div>
            <div className={'mt-1 text-xs text-gray-500'}>
              {data?.staffUtilization.active}名 / {data?.staffUtilization.total}
              名稼働中
            </div>
            <div className={'mt-3 h-2 overflow-hidden rounded bg-gray-200'}>
              <div
                className={'h-full bg-green'}
                style={{ width: `${data?.staffUtilization.rate ?? 0}%` }}
              />
            </div>
          </div>

          <div className={'rounded-md border bg-white p-4 shadow-sm'}>
            <div className={'text-sm text-gray-500'}>平均単価（月額）</div>
            <div className={'mt-2 text-3xl font-bold text-gray-800'}>
              {data?.averageUnitPrice.monthly}
            </div>
            <div
              className={`mt-1 text-xs font-semibold ${
                data?.averageUnitPrice.trendType === 'up'
                  ? 'text-emerald-600'
                  : 'text-red-500'
              }`}
            >
              {data?.averageUnitPrice.trend}
            </div>
          </div>

          <div className={'rounded-md border bg-white p-4 shadow-sm'}>
            <div className={'text-sm text-gray-500'}>待機要員</div>
            <div className={'mt-2 text-3xl font-bold text-gray-800'}>
              {data?.standbyStaff.count}名
            </div>
            <div className={'mt-2 space-y-1'}>
              {data?.standbyStaff.names.map((name) => (
                <span
                  key={name}
                  className={
                    'mr-1.5 inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600'
                  }
                >
                  {name}
                </span>
              ))}
            </div>
          </div>

          <div className={'rounded-md border bg-white p-4 shadow-sm'}>
            <div className={'text-sm text-gray-500'}>新規顧客獲得</div>
            <div className={'mt-2 text-3xl font-bold text-gray-800'}>
              {data?.newCustomers.count}社
            </div>
            <div className={'mt-1 text-xs text-gray-500'}>
              目標: {data?.newCustomers.target}社
            </div>
            <div className={'mt-2 space-y-1'}>
              {data?.newCustomers.list.map((name) => (
                <p key={name} className={'text-xs text-gray-600'}>
                  {name}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* 注力顧客の状況 */}
        <section className={'rounded-md border bg-white p-4 shadow-sm'}>
          <h2 className={'mb-3 text-base font-bold text-gray-700'}>
            注力顧客の状況
          </h2>
          <div className={'grid grid-cols-1 gap-4 xl:grid-cols-3'}>
            {focusCustomers?.map((customer) => (
              <div
                key={customer.companyId}
                className={'rounded border border-gray-200 p-4'}
              >
                <div className={'flex items-start justify-between gap-2'}>
                  <div>
                    <p className={'text-sm font-bold text-gray-800'}>
                      {customer.companyName}
                    </p>
                    <p className={'text-xs text-gray-500'}>
                      {customer.tasks.length}件のタスク
                    </p>
                  </div>
                  <span
                    className={
                      'shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700'
                    }
                  >
                    注力中
                  </span>
                </div>
                <p className={'mt-2 text-xs leading-5 text-gray-600'}>
                  {customer.summary ?? '要約なし'}
                </p>
                <div
                  className={
                    'mt-3 flex items-center gap-4 text-xs text-gray-500'
                  }
                >
                  <span>
                    タスク数:{' '}
                    <strong className={'text-gray-800'}>
                      {customer.tasks.length}
                    </strong>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 最近の営業活動・案件パイプライン */}
        <div className={'grid grid-cols-1 gap-4 xl:grid-cols-2'}>
          <section className={'rounded-md border bg-white p-4 shadow-sm'}>
            <h2 className={'mb-3 text-base font-bold text-gray-700'}>
              最近の営業活動
            </h2>
            <div className={'space-y-3'}>
              {data?.activities.map((activity) => {
                return (
                  <div
                    key={`${activity.title}-${activity.timeAgo}`}
                    className={
                      'flex items-center justify-between border-b border-gray-100 pb-2'
                    }
                  >
                    <div>
                      <p className={'text-sm text-gray-700'}>
                        {activity.title}
                      </p>
                      <p className={'text-xs text-gray-500'}>
                        担当: {activity.owner}
                      </p>
                    </div>
                    <span className={'text-xs text-gray-400'}>
                      {activity.timeAgo}
                    </span>
                  </div>
                )
              })}
            </div>
          </section>

          <section className={'rounded-md border bg-white p-4 shadow-sm'}>
            <h2 className={'mb-3 text-base font-bold text-gray-700'}>
              案件パイプライン（今月）
            </h2>
            <div className={'overflow-x-auto'}>
              <table className={'min-w-full text-sm'}>
                <thead>
                  <tr
                    className={
                      'border-b bg-gray-50 text-left text-xs text-gray-600'
                    }
                  >
                    <th className={'px-2 py-2'}>案件名</th>
                    <th className={'px-2 py-2'}>顧客</th>
                    <th className={'px-2 py-2'}>金額</th>
                    <th className={'px-2 py-2'}>担当</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.pipelines.map((pipeline) => {
                    return (
                      <tr
                        key={pipeline.projectName}
                        className={'border-b last:border-b-0'}
                      >
                        <td className={'px-2 py-2'}>{pipeline.projectName}</td>
                        <td className={'px-2 py-2'}>{pipeline.customer}</td>
                        <td className={'px-2 py-2'}>{pipeline.amount}</td>
                        <td className={'px-2 py-2'}>{pipeline.owner}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* 業種別顧客分布・言語別案件・人材分布 */}
        <div className={'grid grid-cols-1 gap-4 xl:grid-cols-2'}>
          <section className={'rounded-md border bg-white p-4 shadow-sm'}>
            <h2 className={'mb-3 text-base font-bold text-gray-700'}>
              業種別顧客分布
            </h2>
            <div className={'space-y-2'}>
              {data?.industryDistribution.map((item) => (
                <div key={item.label}>
                  <div className={'flex items-center justify-between text-sm'}>
                    <span className={'text-gray-700'}>{item.label}</span>
                    <span className={'text-gray-500'}>
                      {item.count}社 ({item.percentage}%)
                    </span>
                  </div>
                  <div
                    className={'mt-1 h-2 overflow-hidden rounded bg-gray-200'}
                  >
                    <div
                      className={'h-full bg-green'}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className={'rounded-md border bg-white p-4 shadow-sm'}>
            <h2 className={'mb-3 text-base font-bold text-gray-700'}>
              言語別案件・人材分布
            </h2>
            <div className={'space-y-2'}>
              {data?.languageDistribution.map((item) => (
                <div key={item.label}>
                  <div className={'flex items-center justify-between text-sm'}>
                    <span className={'text-gray-700'}>{item.label}</span>
                    <span className={'text-gray-500'}>
                      {item.count}件 ({item.percentage}%)
                    </span>
                  </div>
                  <div
                    className={'mt-1 h-2 overflow-hidden rounded bg-gray-200'}
                  >
                    <div
                      className={'h-full bg-blue-400'}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* インフラ環境別案件分布・作業種別案件分布 */}
        <div className={'grid grid-cols-1 gap-4 xl:grid-cols-2'}>
          <section className={'rounded-md border bg-white p-4 shadow-sm'}>
            <h2 className={'mb-3 text-base font-bold text-gray-700'}>
              インフラ環境別案件分布
            </h2>
            <div className={'space-y-2'}>
              {data?.infraDistribution.map((item) => (
                <div key={item.label}>
                  <div className={'flex items-center justify-between text-sm'}>
                    <span className={'text-gray-700'}>{item.label}</span>
                    <span className={'text-gray-500'}>
                      {item.count}件 ({item.percentage}%)
                    </span>
                  </div>
                  <div
                    className={'mt-1 h-2 overflow-hidden rounded bg-gray-200'}
                  >
                    <div
                      className={'h-full bg-orange-400'}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className={'rounded-md border bg-white p-4 shadow-sm'}>
            <h2 className={'mb-3 text-base font-bold text-gray-700'}>
              作業種別案件分布
            </h2>
            <div className={'space-y-2'}>
              {data?.workTypeDistribution.map((item) => (
                <div key={item.label}>
                  <div className={'flex items-center justify-between text-sm'}>
                    <span className={'text-gray-700'}>{item.label}</span>
                    <span className={'text-gray-500'}>
                      {item.count}件 ({item.percentage}%)
                    </span>
                  </div>
                  <div
                    className={'mt-1 h-2 overflow-hidden rounded bg-gray-200'}
                  >
                    <div
                      className={'h-full bg-purple-400'}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </MainPage>
  )
}
